import {
  Alert,
  Box,
  Button,
  Dialog,
  Typography,
  useTheme,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import SelectProduct from '../select/SelectProduct';
import SelectService from '../select/SelectService';
import Sum from './position-modules/Sum';
import { numberToString } from '../special/numberConverter';
import Trans from '../special/Trans';
import { useTranslation } from 'react-i18next';
import ExoDialog from './ExoDialog';
import { PositionsHeader } from './positions/PositionsHeader';
import { PositionsRowEmpty } from './positions/PositionsRowEmpty';
import { PositionImage } from './positions/collapse-modules/PositionImage';
import { PositionNumber } from './positions/collapse-modules/PositionNumber';
import { PositionPreCalculation } from './positions/collapse-modules/PositionPreCalculation';
import { PositionText } from './positions/collapse-modules/PositionText';
import { PositionDescription } from './positions/collapse-modules/PositionDescription';
import { PositionDiscount } from './positions/PositionDiscount';
import { PositionCopy } from './positions/collapse-modules/PositionCopy';
import { HOAICalculator } from './hoai/HOAICalculator';
import { updateOrCreate } from '../special/updateOrCreate';
import SelectTimeBilling from '../select/SelectTimeBilling';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import Entry from './position-modules/Entry';
import uuid from 'react-uuid';
import { createPortal } from 'react-dom';
import { TableVirtuoso } from 'react-virtuoso';
import { Position } from './positions/Position';
import { round } from '../special/round';
import Controls from './position-modules/Controls';
import { SelectImportPositions } from '../select/SelectImportPositions';

const ExoPositions = forwardRef(
  (
    {
      className,
      onPositionChange,
      startPositions,
      onFocus = () => {},
      descriptionVars = [],
      showVars,
      hideServices,
      hideProducts,
      hideNewPosition,
      hideSum,
      hideGroups,
      hideDiscount,
      hideHOAI,
      hideTimeBilling = true,
      hidePostPositions = false,
      hideImport,
      emptyStart,
      imageType,
      imageApiFilter,
      defaultTaxRate,
      openAmount,
      groupKey = 'name',
      hoaiData,
      title,
      textBlockType,
      skonto,

      collapseModules = [
        { component: PositionImage, key: 'image', label: 'Image' },
        {
          component: PositionDescription,
          key: 'description',
          label: 'Description',
          textBlockType: textBlockType,
        },
        {
          component: PositionNumber,
          key: 'taxRate',
          label: 'Tax Rate',
          unit: '%',
          decimalPlaces: 0,
        },
        {
          component: PositionText,
          key: 'unit',
          label: 'Unit',
        },
        {
          component: PositionCopy,
          label: 'Duplicate position',
        },
        {
          component: PositionPreCalculation,
          key: 'price',
          label: 'Calculation of sales price',
        },
      ],
      columns = [
        {
          text: Trans('Position'),
          width: '80px',
          align: 'left',
          key: 'position',
          className: 'monospace justify-center',
          number: true,
          decimalPlaces: 2,
          editable: false,
          hidden: false,
          default: 1,
        },
        {
          text: Trans('Description'),
          width: 'auto',
          align: 'left',
          key: 'name',
          editable: true,
          hidden: false,
          default: '',
        },
        {
          text: Trans('Amount'),
          width: '80px',
          align: 'center',
          key: 'amount',
          className: 'monospace',
          number: true,
          decimalPlaces: 2,
          editable: true,
          hidden: false,
          default: 0,
        },
        {
          text: Trans('Unit Price'),
          width: '120px',
          align: 'center',
          key: 'price',
          className: 'monospace',
          unit: ' €',
          number: true,
          editable: true,
          hidden: false,
          default: 0,
        },
        {
          text: '',
          key: 'delete',
          width: '37px',
        },
        {
          text: Trans('Sum'),
          width: '130px',
          align: 'right',
          key: 'custom',
          unit: ' €',
          className: 'justify-end',
          hideOnMobile: true,
          hidden: false,
          custom: (element, unit) => {
            var sum = 0;
            if (element.amount && element.price) {
              sum = round(
                parseFloat(element.amount) * parseFloat(element.price)
              );
            }
            return (
              <Box className="w-full text-right monospace">
                {element.isOptional && '('}
                {numberToString(sum)}
                {unit}
                {element.isOptional && ')'}
              </Box>
            );
          },
        },
        {
          key: 'status',
          hidden: true,
          default: 0,
        },
        {
          key: 'description',
          hidden: true,
          default: '',
        },
        {
          key: 'positionableId',
          hidden: true,
          default: null,
        },
        {
          key: 'positionableType',
          hidden: true,
          default: 'custom',
        },
      ],
    },
    ref
  ) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

    const virtuosoRef = useRef();

    const { t } = useTranslation();

    const [items, setItems] = useState([]);
    const [allItems, setAllItems] = useState([]);
    const [defaultPosition, setDefaultPosition] = useState(null);
    const [openHOAI, setOpenHOAI] = useState(null);
    const [hoais, setHoais] = useState(hoaiData ?? []);
    const [openTimeBilling, setOpenTimeBilling] = useState(null);
    const [openImport, setOpenImport] = useState(false);

    const posColWidth = 15; //In pixels, one pixel is added for the border

    const [dynamicColumns, setDynamicColumns] = useState([...columns]);

    const [sortIds, setSortIds] = useState([]);
    const [activeItem, setActiveItem] = useState(null);
    const [projectedIndex, setProjectedIndex] = useState(null);
    const [horizontalDelta, setHorizontalDelta] = useState(0);

    const [alertState, setAlertState] = useState({
      alertOpen: false,
      item: null,
    });

    function checkSpecialPositions(pos) {
      //When adjusting make sure to adjust in Position.jsx as well
      return pos.isPostPosDivider || pos.isLastPlaceholder || pos.isSum;
    }

    function handlePositionChange(positions, hoaiData) {
      const cleanedPos = positions.filter((el) => !checkSpecialPositions(el));

      onPositionChange(cleanedPos, hoaiData);
    }

    function checkSortCorrectness(items) {
      const expected = new Set(
        Array.from({ length: items.length }, (_, i) => i + 1)
      );
      const actual = new Set(items.map((obj) => obj.sortId));

      return (
        expected.size === actual.size &&
        [...expected].every((id) => actual.has(id))
      );
    }

    function migrateLegacyPositions(items) {
      let seenGroup = false;
      const newItems = items;
      for (let i = 0; i < items.length; i++) {
        if (newItems[i].depth == null) {
          if (newItems[i].isGroup) {
            seenGroup = true;
            newItems[i].depth = 0;
          } else {
            if (seenGroup) {
              newItems[i].depth = 1;
            } else {
              newItems[i].depth = 0;
            }
          }
        }
      }
      return newItems;
    }

    function createSortIds(newItems) {
      let update = false;

      if (
        !hidePostPositions &&
        newItems.findIndex((el) => el.isPostPosDivider) == -1
      ) {
        update = true;

        const lastNonPostPosIndex = newItems.reduce(
          (lastIndex, el, idx) =>
            el.isPostPosition === false ? idx : lastIndex,
          -1
        );

        const insertIndex =
          lastNonPostPosIndex === -1 ? 0 : lastNonPostPosIndex + 1;

        newItems = [
          ...newItems.slice(0, insertIndex),
          {
            isPostPosDivider: true,
            keyId: 'PostPosDivider',
            depth: 0,
          },
          ...newItems.slice(insertIndex),
        ];
      }

      if (newItems.findIndex((el) => el.isLastPlaceholder) == -1) {
        update = true;

        newItems = [
          ...newItems,
          {
            isLastPlaceholder: true,
            keyId: 'LastPlaceholder',
            depth: 0,
          },
        ];
      }

      if (newItems.findIndex((el) => el.isSum) == -1) {
        update = true;

        newItems = [
          ...newItems,
          {
            isSum: true,
            keyId: 'sumPos',
            depth: 0,
          },
        ];
      }

      if (newItems.some((item) => !('depth' in item))) {
        newItems = migrateLegacyPositions(newItems);
        update = true;
      }

      if (!checkSortCorrectness(newItems)) {
        newItems = newItems.map((item, index) => {
          return { ...item, sortId: index + 1 };
        });
        update = true;
      }

      newItems = newItems.map((item) => {
        if (!item.keyId) update = true;
        let newKeyId = item.keyId ?? item.id ?? uuid();
        if (!isNaN(newKeyId)) {
          newKeyId = 'keyId' + newKeyId;
        }
        return { ...item, keyId: newKeyId };
      });

      if (update) setItems(calculateNewPositions(newItems, true));

      const newSortItems = newItems.map((item) => {
        return item.sortId;
      });

      setSortIds(newSortItems);
    }

    const [sum, setSum] = useState({
      netto: 0,
      tax: 0,
    });

    function recalculateColumns(maxDepth) {
      let newColumns = columns;

      const index = newColumns.findIndex((el) => el.key == 'position');

      if (index == -1) {
        return;
      }

      newColumns[index] = {
        ...newColumns[index],
        key: 'position0',
        colSpan: maxDepth + 1,
        width: `${posColWidth}px`,
      };

      for (let i = 1; i <= maxDepth; i++) {
        newColumns.splice(index + i, 0, {
          key: 'position' + i,
          width: i == maxDepth ? `${maxDepth * 8}px` : `${posColWidth}px`,
        });
      }

      setDynamicColumns(newColumns);
    }

    useEffect(() => {
      if (startPositions && startPositions.length > 0)
        createSortIds(startPositions);
    }, [startPositions]);

    useEffect(() => {
      var newDefaultPositions = {};
      columns.forEach((column) => {
        if (!column.key.match(/position\d+/)) {
          newDefaultPositions[column.key] = column.default;
        }
      });
      newDefaultPositions.taxRate =
        defaultTaxRate === undefined || defaultTaxRate === null
          ? 19
          : defaultTaxRate;
      newDefaultPositions.depth = 0;
      newDefaultPositions.type = 'default';

      setDefaultPosition(newDefaultPositions);
      if (!(startPositions && startPositions.length)) createSortIds(items);
    }, []);

    useEffect(() => {
      if (emptyStart) return;
      if (items.length === 2 + (hidePostPositions ? 0 : 1) && defaultPosition) {
        handleNewPosition(defaultPosition);
      }
    }, [defaultPosition]);

    function moveGroupSortIds(sortIds, groupSortIds, fromIndex, toIndex) {
      const filtered = sortIds.filter((id) => !groupSortIds.includes(id));

      const insertIndex =
        toIndex >= fromIndex ? toIndex - groupSortIds.length + 1 : toIndex;

      return [
        ...filtered.slice(0, insertIndex),
        ...groupSortIds,
        ...filtered.slice(insertIndex),
      ];
    }

    function handleOnDragMove(event) {
      setHorizontalDelta(event.delta.x);
    }

    const handleOnDragStart = (event) => {
      const itemIndex = items.findIndex(
        (item) => item.sortId === event.active.id
      );
      setActiveItem(
        itemIndex != -1 ? { ...items[itemIndex], index: itemIndex } : null
      );
      if (items[itemIndex].isGroup) {
        const newItems = items;
        newItems[itemIndex].expandStatus = 1;
        setItems(calculateNewPositions(newItems));
      }
    };

    function handleOnDragOver(event) {
      const overId = event.over?.id;
      if (!overId) return null;
      const overIndex = items.findIndex((item) => item.sortId === overId);
      if (
        (activeItem.isGroup || activeItem.type == 'hoaiPhase') &&
        items[overIndex].isPostPosition
      ) {
        return;
      }

      setProjectedIndex(overIndex === -1 ? null : overIndex);
    }

    const handleOnDragEnd = (event) => {
      const { active, over, delta } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = sortIds.findIndex((id) => id === active.id);
      let newIndex = sortIds.findIndex((id) => id === over.id);

      let addGroup = false;
      if (
        (activeItem.isGroup || activeItem.type == 'hoaiPhase') &&
        allItems[newIndex].isPostPosition
      ) {
        newIndex = projectedIndex;
        addGroup = activeItem.isGroup;
      }

      newIndex = newIndex >= oldIndex ? newIndex - 1 : newIndex;

      let groupSortIds = [allItems[oldIndex].sortId];
      if (allItems[oldIndex].isGroup && allItems[oldIndex].expandStatus > 0) {
        let counter = 1;
        while (allItems[oldIndex + counter].depth > allItems[oldIndex].depth) {
          groupSortIds.push(allItems[oldIndex + counter].sortId);
          counter++;
        }
      }

      const newSortIds =
        groupSortIds.length > 1
          ? moveGroupSortIds(
              sortIds,
              groupSortIds,
              oldIndex,
              addGroup ? newIndex + groupSortIds.length - 1 : newIndex
            )
          : arrayMove(sortIds, oldIndex, newIndex);
      setSortIds(newSortIds);

      const reorderedItems = newSortIds.map((sortId) =>
        allItems.find((item) => item.sortId === sortId)
      );

      newIndex = reorderedItems.findIndex((item) => item.sortId === active.id);

      const oldDepth = reorderedItems[newIndex].depth;

      reorderedItems[newIndex].depth = calculateDepthFromDelta(
        delta.x,
        reorderedItems[newIndex].depth,
        [
          newIndex == 0
            ? { isGroup: false, depth: 0 }
            : reorderedItems[newIndex - 1],
          newIndex - 1 >= reorderedItems.length
            ? { isGroup: false, depth: 0 }
            : reorderedItems[newIndex + groupSortIds.length],
        ]
      );

      for (let i = 1; i < groupSortIds.length; i++) {
        reorderedItems[newIndex + i].depth +=
          reorderedItems[newIndex].depth - oldDepth;
      }

      if (activeItem.isGroup) {
        reorderedItems[newIndex].expandStatus = activeItem.expandStatus;
      }

      setActiveItem(null);
      setProjectedIndex(null);

      setItems(calculateNewPositions(reorderedItems, true, true));

      setHorizontalDelta(0);
    };

    function handleNewPosition(position, afterDivider) {
      if (position) {
        position = {
          ...position,
          keyId: uuid(),
          sortId: null,
          depth: 0,
          expand: position.isGroup ? null : false,
        };
      }

      const maxSortId = Math.max(...allItems.map((el) => el.sortId));

      const newOrderedPositions = insertPositions(
        allItems,
        [
          {
            ...{ ...defaultPosition, keyId: uuid() },
            ...position,
            sortId: maxSortId + 1,
          },
        ],
        [maxSortId + 1],
        afterDivider || position.isPostPosition
      );

      setItems(newOrderedPositions);

      virtuosoRef.current?.scrollToIndex({
        index: afterDivider
          ? newOrderedPositions.length
          : newOrderedPositions.findIndex((el) => el.isPostPosDivider) + 1,
        align: 'center',
      });
    }

    function handleNewGroup() {
      var newGroup = {
        [groupKey]: '',
        isGroup: true,
        amount: 0,
        unit: '',
        price: 0,
        taxRate: 0,
        positionableId: null,
        positionableType: 'custom',
        taskTitle: '',
        pricePerHour: '',
        monthlyLimit: 0,
        status: 0,
      };
      handleNewPosition(newGroup);
    }

    function calculateNewPositions(
      positions,
      updateAllItems,
      triggerPositionChange
    ) {
      let newAllItems =
        allItems.length == 0 || updateAllItems ? positions : allItems;
      let newPositions = newAllItems;

      let maxDepth = Math.max(...newPositions.map((obj) => obj.depth)) + 1;
      if (isNaN(maxDepth) || maxDepth < 0) maxDepth = 1;

      const counters = Array(maxDepth).fill(0);
      let currentDepth = 0;

      newPositions.forEach((pos) => {
        if (!pos.isPostPosDivider) {
          if (currentDepth == pos.depth) {
            counters[pos.depth]++;
          } else if (currentDepth < pos.depth) {
            currentDepth = pos.depth;
            counters[pos.depth]++;
          } else if (currentDepth > pos.depth) {
            currentDepth = pos.depth;
            counters[pos.depth]++;
            counters.fill(0, pos.depth + 1);
          }

          pos.position = counters.filter((value) => value !== 0).join('.');
        }
      });

      //Set isPostPosition for all Positions
      let isBeforeDivider = true;
      for (let i = 0; i < newPositions.length; i++) {
        if (isBeforeDivider) {
          newPositions[i].isPostPosition = false;
          if (newPositions[i].isPostPosDivider) {
            isBeforeDivider = false;
          }
        } else {
          newPositions[i].isPostPosition = true;
        }
      }

      let subtotalsBrutto = Array(maxDepth).fill(0);
      let subtotalsNetto = Array(maxDepth).fill(0);
      let prevDepth = 0;
      let percentPosToUpdate = [];
      let outerSumNetto = 0;
      let outerSumBrutto = 0;
      let totalNetto = 0;
      let totalBrutto = 0;

      for (let i = newPositions.length - 1; 0 <= i; i--) {
        const currentPos = newPositions[i];

        if (prevDepth > currentPos.depth && currentPos.isGroup) {
          newPositions[i].expandStatus =
            currentPos.expandStatus == 0 || currentPos.expandStatus == null
              ? 2
              : currentPos.expandStatus;
        } else if (currentPos.isGroup) {
          newPositions[i].expandStatus = 0;
        }
        prevDepth = newPositions[i].depth;

        if (currentPos.type == 'percent') {
          percentPosToUpdate.push(i);
        } else if (currentPos.isGroup) {
          let additionalPercentPriceNetto = 0;
          let additionalPercentPriceBrutto = 0;
          for (let k = percentPosToUpdate.length - 1; k >= 0; k--) {
            if (
              newPositions[percentPosToUpdate[k]].depth - 1 ==
              currentPos.depth
            ) {
              newPositions[percentPosToUpdate[k]].price =
                subtotalsNetto[currentPos.depth];
              additionalPercentPriceNetto += round(
                subtotalsNetto[currentPos.depth] *
                  newPositions[percentPosToUpdate[k]].amount
              );
              additionalPercentPriceBrutto += round(
                subtotalsBrutto[currentPos.depth] *
                  newPositions[percentPosToUpdate[k]].amount
              );
              newPositions[percentPosToUpdate[k]].bruttoPrice =
                subtotalsBrutto[currentPos.depth];

              newPositions[percentPosToUpdate[k]].taxRate = Math.round(
                (subtotalsBrutto[currentPos.depth] /
                  subtotalsNetto[currentPos.depth] -
                  1) *
                  100
              );

              percentPosToUpdate = percentPosToUpdate.filter(
                (el) => el != percentPosToUpdate[k]
              );
            }
          }

          if (newPositions[i].isPostPosition) {
            totalNetto += additionalPercentPriceNetto;
            totalBrutto += additionalPercentPriceBrutto;
          } else {
            outerSumNetto += additionalPercentPriceNetto;
            outerSumBrutto += additionalPercentPriceBrutto;
          }
          newPositions[i].sumNetto =
            subtotalsNetto[currentPos.depth] + additionalPercentPriceNetto;

          newPositions[i].sumBrutto =
            subtotalsBrutto[currentPos.depth] + additionalPercentPriceBrutto;

          subtotalsNetto[currentPos.depth] = 0;
          subtotalsBrutto[currentPos.depth] = 0;

          for (let j = 0; j < currentPos.depth; j++) {
            subtotalsBrutto[j] += additionalPercentPriceBrutto;
            subtotalsNetto[j] += additionalPercentPriceNetto;
          }
        } else if (!currentPos.isOptional) {
          for (let i = 0; i < currentPos.depth; i++) {
            subtotalsNetto[i] += round(
              Number(currentPos.amount) * Number(currentPos.price)
            );
            subtotalsBrutto[i] += round(
              round(Number(currentPos.amount) * Number(currentPos.price)) *
                (1 + currentPos.taxRate / 100)
            );
          }
          if (currentPos.isPostPosition) {
            totalBrutto += round(
              round(
                Number(currentPos.amount ?? 0) * Number(currentPos.price ?? 0)
              ) *
                (1 + (currentPos.taxRate ?? 0) / 100)
            );

            totalNetto += round(
              Number(currentPos.amount ?? 0) * Number(currentPos.price ?? 0)
            );
          } else {
            outerSumBrutto += round(
              Number(currentPos.amount ?? 0) *
                Number(currentPos.price ?? 0) *
                (1 + (currentPos.taxRate ?? 0) / 100)
            );

            outerSumNetto += round(
              Number(currentPos.amount ?? 0) * Number(currentPos.price ?? 0)
            );
          }
        }
      }

      totalNetto += outerSumNetto;
      totalBrutto += outerSumBrutto;

      let preDividerPercentSumNetto = 0;
      let preDividerPercentSumBrutto = 0;

      for (let k = percentPosToUpdate.length - 1; k >= 0; k--) {
        if (!newPositions[percentPosToUpdate[k]].isPostPosition) {
          newPositions[percentPosToUpdate[k]].price = outerSumNetto;
          newPositions[percentPosToUpdate[k]].bruttoPrice = outerSumBrutto;
          totalNetto += round(
            outerSumNetto * newPositions[percentPosToUpdate[k]].amount
          );
          preDividerPercentSumNetto += round(
            outerSumNetto * newPositions[percentPosToUpdate[k]].amount
          );
          totalBrutto += round(
            outerSumBrutto * newPositions[percentPosToUpdate[k]].amount
          );
          preDividerPercentSumBrutto += round(
            outerSumBrutto * newPositions[percentPosToUpdate[k]].amount
          );

          newPositions[percentPosToUpdate[k]].taxRate = Math.round(
            (outerSumBrutto / outerSumNetto - 1) * 100
          );
        } else {
          newPositions[percentPosToUpdate[k]].price =
            outerSumNetto + preDividerPercentSumNetto;
          newPositions[percentPosToUpdate[k]].bruttoPrice =
            outerSumBrutto + preDividerPercentSumBrutto;

          totalNetto += round(
            newPositions[percentPosToUpdate[k]].price *
              newPositions[percentPosToUpdate[k]].amount
          );
          totalBrutto += round(
            newPositions[percentPosToUpdate[k]].bruttoPrice *
              newPositions[percentPosToUpdate[k]].amount
          );

          newPositions[percentPosToUpdate[k]].taxRate = Math.round(
            (round(
              newPositions[percentPosToUpdate[k]].bruttoPrice *
                newPositions[percentPosToUpdate[k]].amount
            ) /
              round(
                newPositions[percentPosToUpdate[k]].price *
                  newPositions[percentPosToUpdate[k]].amount
              ) -
              1) *
              100
          );
        }
        percentPosToUpdate = percentPosToUpdate.filter(
          (el) => el != percentPosToUpdate[k]
        );
      }

      //Calculate Sum for PostPosDivider
      let sum = 0;
      for (let i = 0; i < newPositions.length; i++) {
        if (newPositions[i].depth == 0) {
          if (newPositions[i].isPostPosDivider) {
            newPositions[i].sumNetto = sum;
            break;
          }
          if (!newPositions[i].isOptional) {
            sum += newPositions[i].isGroup
              ? newPositions[i].sumNetto
              : round(newPositions[i].price * newPositions[i].amount);
          }
        }
      }

      setSum({ netto: totalNetto, tax: totalBrutto - totalNetto });

      //Calculate whether the current element should be shown based on the groups expand status
      let currentExpandDepth = 0;
      for (let i = 0; i < newPositions.length; i++) {
        const currentPos = newPositions[i];
        if (currentExpandDepth < currentPos.depth) {
          newPositions[i].hidden = true;
        } else {
          newPositions[i].hidden = false;
        }
        if (!currentPos.hidden) {
          if (currentPos.isGroup && currentPos.expandStatus == 2) {
            currentExpandDepth = currentPos.depth + 1;
          } else {
            currentExpandDepth = currentPos.depth;
          }
        }
      }
      newPositions = newPositions.filter((el) => !el.hidden);

      if (triggerPositionChange) handlePositionChange(newAllItems);

      setAllItems(newAllItems);

      recalculateColumns(maxDepth);

      return newPositions;
    }

    function insertPositions(
      items,
      insertedItems,
      insertedSortIds,
      afterDivider,
      givenIndex
    ) {
      const postPosDividerIndex = items.findIndex((el) => el.isPostPosDivider);

      const index =
        givenIndex ??
        (postPosDividerIndex >= 0 && !afterDivider ? postPosDividerIndex : -2);

      const newSortIds = [
        ...sortIds.slice(0, index),
        ...insertedSortIds,
        ...sortIds.slice(index),
      ];

      const newItems = [
        ...items.slice(0, index),
        ...insertedItems,
        ...items.slice(index),
      ];

      setSortIds(newSortIds);
      return calculateNewPositions(newItems, true, true);
    }

    function handleDelete(item) {
      if (item.isGroup) {
        if (item.expandStatus == 0) {
          handleGroupDelete(item, false);
        } else {
          setAlertState({ alertOpen: true, item: item });
        }
      } else {
        const newSortIds = sortIds.filter((id) => id != item.sortId);
        const newItems = allItems.filter((el) => el.keyId != item.keyId);
        setItems(calculateNewPositions(newItems, true, true));
        setSortIds(newSortIds);
      }
    }

    function handleGroupDelete(item, deleteChildren) {
      setAlertState({ alertOpen: false, item: null });
      let newItems = allItems;
      let newSortIds = sortIds;

      const index = allItems.findIndex((el) => el.keyId == item.keyId);

      let groupKeys = [item.keyId];
      let groupSortIds = [item.sortId];
      if (item.expandStatus > 0) {
        let counter = 1;
        while (allItems[index + counter].depth > allItems[index].depth) {
          groupKeys.push(allItems[index + counter].keyId);
          groupSortIds.push(allItems[index + counter].sortId);
          counter++;
        }
      }

      if (deleteChildren) {
        newItems = newItems.filter((el) => !groupKeys.includes(el.keyId));
        newSortIds = newSortIds.filter((id) => !groupSortIds.includes(id));
      } else {
        newItems = newItems.map((el) => {
          return groupKeys.includes(el.keyId)
            ? { ...el, depth: el.depth - 1 }
            : el;
        });
        newSortIds = newSortIds.filter((id) => id != item.sortId);
        newItems = newItems.filter((el) => el.keyId != item.keyId);
      }
      setSortIds(newSortIds);
      setItems(calculateNewPositions(newItems, true, true));
    }

    // product
    const [openProducts, setOpenProducts] = useState(null);
    function handleProductSelect(product, afterDivider) {
      const productEntry = {
        productNr: product.nr,
        status: 0,
        position: 1,
        name: product.name,
        image: product.image,
        description: product.description,
        amount: 0,
        unit: product.unit,
        price: product.price,
        taxRate: product.taxRate,
        positionableId: product.id,
        positionableType: 'product',
        taskTitle: product.name,
        pricePerHour: product.price,
        monthlyLimit: 0,
      };
      handleNewPosition(productEntry, afterDivider);
    }

    // service
    const [openServices, setOpenServices] = useState(null);
    function handleServiceSelect(service, afterDivider) {
      const serviceEntry = {
        serviceNr: service.nr,
        status: 0,
        position: 1,
        name: service.name,
        image: service.image,
        description: service.description,
        amount: 0,
        unit: service.unit,
        price: service.price,
        taxRate: service.taxRate,
        positionableId: service.id,
        positionableType: 'service',
        taskTitle: service.name,
        pricePerHour: service.price,
        monthlyLimit: 0,
      };
      handleNewPosition(serviceEntry, afterDivider);
    }

    // exposed functions
    useImperativeHandle(ref, () => ({
      setItems: setItems,
      items: items,
    }));

    // discount
    const [openDiscount, setOpenDiscount] = useState(false);
    function handleNewDiscount(newDiscount) {
      handleNewPosition({
        ...defaultPosition,
        price: newDiscount.discount * -1,
        amount: 1,
        name: newDiscount.name,
      });
    }

    // hoai
    function removeHOAIPositions(hoaiId) {
      return allItems.filter((item) => item.hoaiId != hoaiId);
    }

    function onEditHOAI(item) {
      const hoaiData = hoais.find((el) => item.hoaiId === el.id);
      if (hoaiData && Object.keys(hoaiData.length > 0)) {
        setOpenHOAI({ ...hoaiData, hoaiTitle: item.name });
      } else setOpenHOAI({ id: item.hoaiId });
    }

    function processSiemonTitle(title, threshold = 6) {
      const words = title.trim().split(/\s+/);

      if (words.length <= threshold) {
        return { title, description: '' };
      }

      const truncated = words.slice(0, threshold).join(' ');
      return { title: truncated + '...', description: title };
    }

    function handleNewHOAI(data) {
      const oldPosition = allItems.find(
        (el) => el.hoaiId == (data.oldHoaiId || data.id)
      );

      const oldPositionIndex = oldPosition
        ? allItems.findIndex((el) => oldPosition.keyId === el.keyId)
        : null;

      const clearedItems = removeHOAIPositions(data.oldHoaiId || data.id);
      var newPositions = [];
      const hoaiData = data.hoaiData;

      var hoaiGroup = {
        [groupKey]: /*t('Honorarium from HOAI') + ' - ' +*/ data.hoaiTitle,
        isGroup: true,
        amount: 0,
        unit: '',
        price: 0,
        taxRate: 0,
        positionableId: null,
        positionableType: 'custom',
        taskTitle: '',
        pricePerHour: '',
        monthlyLimit: 0,
        status: 0,
        editableHOAI: true,
        hoaiId: data.id,
        depth: 0,
        hoaiType: 'group',
      };
      newPositions.push(hoaiGroup);

      let groupCounter = 1;
      /*
        if (hoaiData.incidentalCosts) groupCounter++;
      //if (hoaiData.buildingSubstance) groupCounter++;
      //if (hoaiData.tgaShare) groupCounter++;

      for (let i = 1; i < groupCounter; i++) {
        var newGroup = {
          [groupKey]: t('Subtotal'),
          isGroup: true,
          amount: 0,
          unit: '',
          price: 0,
          taxRate: 0,
          positionableId: null,
          positionableType: 'custom',
          taskTitle: '',
          pricePerHour: '',
          monthlyLimit: 0,
          status: 0,
          hoaiId: data.id,
          depth: i,
          hoaiType: 'subtotalGroup',
        };
        newPositions.push(newGroup);
      }*/

      var newGroup = {
        [groupKey]: t('Phases') + ' - ' + hoaiData.label,
        isGroup: true,
        amount: 0,
        unit: '',
        price: 0,
        taxRate: 0,
        positionableId: null,
        positionableType: 'custom',
        taskTitle: '',
        pricePerHour: '',
        monthlyLimit: 0,
        status: 0,
        hoaiId: data.id,
        depth: groupCounter,
        hoaiType: 'phasesGroup',
      };
      newPositions.push(newGroup);

      hoaiData.phases.forEach((phase, index) => {
        if (phase.isCrossed) return;
        if (hoaiData.showSiemon && phase.siemon) {
          newPositions.push({
            [groupKey]: `${index + 1}. ${phase.title}`,
            isGroup: true,
            amount: 0,
            unit: '',
            price: 0,
            taxRate: 0,
            positionableId: null,
            positionableType: 'custom',
            taskTitle: '',
            pricePerHour: '',
            monthlyLimit: 0,
            status: 0,
            hoaiId: data.id,
            depth: groupCounter + 1,
            hoaiType: 'siemonGroup',
          });

          phase.siemon.forEach((siemon) => {
            const { title, description } = processSiemonTitle(siemon.title);
            newPositions.push({
              ...defaultPosition,
              price: hoaiData.baseHonorarium,
              amount: (siemon.siemonValue ?? siemon.maxPercent) / 100,
              name: `${title}`,
              taxRate: hoaiData.vat ?? defaultPosition.taxRate,
              hoaiId: data.id,
              description: description,
              depth: groupCounter + 2,
              type: 'hoaiPhase',
              hoaiType: 'siemonPhase',
            });
          });
        } else
          newPositions.push({
            ...defaultPosition,
            price: hoaiData.baseHonorarium,
            amount: (phase.value ?? phase.maxPercent) / 100,
            name: `${index + 1}. ${phase.title}`,
            taxRate: hoaiData.vat ?? defaultPosition.taxRate,
            hoaiId: data.id,
            description: phase.description,
            depth: groupCounter + 1,
            type: 'hoaiPhase',
            hoaiType: 'phase',
          });
      });

      hoaiData.surcharge &&
        newPositions.push({
          ...defaultPosition,
          amount: hoaiData.surcharge / 100,
          name: t('Surcharge'),
          taxRate: 0,
          hoaiId: data.id,
          depth: groupCounter + 1,
          type: 'percent',
          hoaiType: 'surcharge',
        });

      var additionalCostsPositions = [];

      let currentAdditionalCostDepth = groupCounter;

      hoaiData.timeFee &&
        additionalCostsPositions.push({
          ...defaultPosition,
          price: hoaiData.timeFee,
          amount: 1,
          name: t('Time fee'),
          taxRate: hoaiData.vat ?? defaultPosition.taxRate,
          hoaiId: data.id,
          depth: currentAdditionalCostDepth,
          hoaiType: 'timeFee',
        });
      hoaiData.specialServices &&
        additionalCostsPositions.push({
          ...defaultPosition,
          price: hoaiData.specialServices,
          amount: 1,
          name: t('Special Services'),
          taxRate: hoaiData.vat ?? defaultPosition.taxRate,
          hoaiId: data.id,
          depth: currentAdditionalCostDepth,
          hoaiType: 'specialServices',
        });
      hoaiData.additionalServices &&
        additionalCostsPositions.push({
          ...defaultPosition,
          price: hoaiData.additionalServices,
          amount: 1,
          name: t('Additional Services'),
          taxRate: hoaiData.vat ?? defaultPosition.taxRate,
          hoaiId: data.id,
          depth: currentAdditionalCostDepth,
          hoaiType: 'additionalServices',
        });
      if (hoaiData.incidentalCostAmount) {
        additionalCostsPositions.push({
          ...defaultPosition,
          amount: hoaiData.incidentalCosts / 100,
          name: t('Incidental Costs'),
          taxRate: hoaiData.vat ?? defaultPosition.taxRate,
          hoaiId: data.id,
          depth: currentAdditionalCostDepth,
          type: 'percent',
          hoaiType: 'incidentalCosts',
        });
        currentAdditionalCostDepth--;
      }
      /*if (hoaiData.buildingSubstanceAmount) {
        additionalCostsPositions.push({
          ...defaultPosition,
          amount: hoaiData.buildingSubstance / 100,
          name: t('Building substance to be processed'),
          taxRate: hoaiData.vat ?? defaultPosition.taxRate,
          hoaiId: data.id,
          depth: currentAdditionalCostDepth,
          type: 'percent',
          hoaiType: 'buildingSubstance',
        });
        currentAdditionalCostDepth--;
      }
      if (hoaiData.tgaShareAmount) {
        additionalCostsPositions.push({
          ...defaultPosition,
          amount: hoaiData.tgaShare / 100,
          name: t('Share of TGA'),
          taxRate: hoaiData.vat ?? defaultPosition.taxRate,
          hoaiId: data.id,
          depth: currentAdditionalCostDepth,
          type: 'percent',
          hoaiType: 'tgaShare',
        });
        currentAdditionalCostDepth--;
      }*/

      if (additionalCostsPositions.length > 0) {
        newPositions = newPositions.concat(additionalCostsPositions);
      }

      var newItems = [...clearedItems];
      if (clearedItems.length == 4 && clearedItems[0].name == '') {
        newItems = [clearedItems[1], clearedItems[2], clearedItems[3]];
      }

      const maxSortId = Math.max(...newItems.map((el) => el.sortId));

      const newSortIdsList = [];

      var hoaiPositions = [];
      newPositions.forEach((newPos, index) => {
        newSortIdsList.push(maxSortId + index + 1);
        hoaiPositions.push({
          ...defaultPosition,
          ...newPos,
          sortId: maxSortId + index + 1,
          keyId: uuid(),
        });
      });

      const newOrderedPositions = insertPositions(
        newItems,
        hoaiPositions,
        newSortIdsList,
        false,
        oldPositionIndex ? oldPositionIndex : null
      );

      const newHoais = updateOrCreate(hoais, data);

      setHoais(newHoais);
      handlePositionChange(newOrderedPositions, newHoais);
      setItems(newOrderedPositions);
    }

    function handleImportPositions(positions) {
      const postPos = positions.filter((el) => el.isPostPosition);
      positions = positions.filter((el) => !el.isPostPosition);

      const maxSortId = Math.max(...items.map((el) => el.sortId));
      var newSortIdsList = [];

      //Normal positions
      var newPositions = [];
      positions.forEach((newPos, index) => {
        newSortIdsList.push(maxSortId + index + 1);
        newPositions.push({
          ...defaultPosition,
          ...newPos,
          sortId: maxSortId + index + 1,
          keyId: newPos.keyId ?? uuid(),
        });
      });

      const newOrderedPositions = insertPositions(
        items,
        newPositions,
        newSortIdsList
      );

      //Post positions
      newPositions = [];
      newSortIdsList = [];
      postPos.forEach((newPos, index) => {
        newSortIdsList.push(maxSortId + index + 1);
        newPositions.push({
          ...defaultPosition,
          ...newPos,
          sortId: maxSortId + index + 1,
          keyId: newPos.keyId ?? uuid(),
        });
      });

      const finalOrderedPositions = insertPositions(
        newOrderedPositions,
        newPositions,
        newSortIdsList,
        true
      );

      handlePositionChange(finalOrderedPositions);
      setItems(finalOrderedPositions);
      setOpenImport(false);
    }

    function handleNewPositions(positionsToAdd, afterDivider) {
      const maxSortId = Math.max(...items.map((el) => el.sortId));

      const newSortIdsList = [];

      var newPositionsToAdd = [];
      positionsToAdd.forEach((newPos, index) => {
        newSortIdsList.push(maxSortId + index + 1);
        newPositionsToAdd.push({
          ...defaultPosition,
          ...newPos,
          sortId: maxSortId + index + 1,
          keyId: uuid(),
        });
      });

      const newOrderedPositions = insertPositions(
        items,
        newPositionsToAdd,
        newSortIdsList,
        afterDivider
      );

      handlePositionChange(newOrderedPositions);
      setItems(newOrderedPositions);
    }

    const mouseSensor = useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    });
    const touchSensor = useSensor(TouchSensor, {
      activationConstraint: {
        delay: 500,
        tolerance: 5,
        distance: 10,
      },
    });
    const sensors = useSensors(mouseSensor, touchSensor);

    const clamp = (value, min, max) =>
      value < min ? min : value > max ? max : value;

    function calculateDepthFromDelta(
      horizontalDelta,
      originalDepth,
      [prev, next]
    ) {
      const { depth: prevDepth, isGroup } = prev;
      const { depth: nextDepth } = next;

      const offset = Math.round(horizontalDelta / posColWidth);
      const tentativeDepth = originalDepth + offset;

      const maxDepth = isGroup
        ? prevDepth + 1
        : prevDepth === -1
        ? 0
        : prevDepth;

      return clamp(tentativeDepth, nextDepth, maxDepth);
    }

    function customOffset({ activatorEvent, draggingNodeRect, transform }) {
      return {
        ...transform,
        x: transform.x + 100,
      };
    }

    const handleKeyDown = useCallback((e) => {
      const virtuoso = virtuosoRef.current;
      if (!virtuoso) return;

      const SCROLL_STEP = 60; //scroll step size

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        virtuoso.scrollBy({ top: SCROLL_STEP, behavior: 'smooth' });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        virtuoso.scrollBy({ top: -SCROLL_STEP, behavior: 'smooth' });
      }
    }, []);

    // Attach global listener
    useEffect(() => {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    return (
      <Box>
        <ExoDialog open={openHOAI}>
          <Box className="p-10">
            <HOAICalculator
              onClose={() => setOpenHOAI(null)}
              onSubmit={handleNewHOAI}
              startDataCollection={openHOAI}
              hoaiTitle={openHOAI?.title ?? title}
            />
          </Box>
        </ExoDialog>
        <DndContext
          onDragEnd={handleOnDragEnd}
          onDragOver={handleOnDragOver}
          onDragStart={handleOnDragStart}
          onDragMove={handleOnDragMove}
          sensors={sensors}
          collisionDetection={closestCenter}
        >
          <SortableContext
            items={sortIds}
            strategy={verticalListSortingStrategy}
            useDragOverlay={true}
            disabled={
              items.filter((el) => !checkSpecialPositions(el)).length <= 1
            }
          >
            <Box
              className={`${className} relative ${
                activeItem ? 'select-none' : ''
              }`}
              sx={{
                '& .MuiAccordionSummary-content': {
                  margin: 0,
                },
                '& .MuiAccordionSummary-root': {
                  minHeight: '26px',
                },
                '& th:first-child': {
                  borderTopLeftRadius: '8px',
                },
                '& th:last-child': {
                  borderTopRightRadius: '8px',
                },
                '& tr:last-child>td:first-child': {
                  borderBottomLeftRadius: '8px',
                },
                '& tr:last-child>td:last-child': {
                  borderBottomRightRadius: '8px',
                },
                '& td, th': {
                  backgroundColor: colors.bgInput,
                },
                '& .position-group td': {
                  backgroundColor: colors.selected,
                },
                '& .order-position td': {
                  background: `linear-gradient(${colors.selected}40, ${colors.selected}40), linear-gradient(${colors.bgInput}, ${colors.bgInput})`,
                },
              }}
            >
              <Box>
                <TableVirtuoso
                  ref={virtuosoRef}
                  context={{
                    items,
                    activeItem,
                    projectedIndex,
                    dynamicColumns,
                    horizontalDelta,
                    posColWidth,
                    groupKey,
                    collapseModules,
                    imageType,
                    imageApiFilter,
                    descriptionVars,
                    showVars,
                    openAmount,
                    calculateDepthFromDelta,
                    onFocus,
                    setItems,
                    calculateNewPositions,
                    handlePositionChange,
                    onEditHOAI,
                    handleDelete,
                    handleNewPosition,

                    hideGroups: Boolean(hideGroups),
                    hideSum: Boolean(hideSum),
                    hideServices: Boolean(hideServices),
                    hideProducts: Boolean(hideProducts),
                    hideNewPosition: Boolean(hideNewPosition),
                    hideDiscount: Boolean(hideDiscount),
                    hideHOAI: hideHOAI,
                    hideTimeBilling: hideTimeBilling,
                    hidePostPositions: hidePostPositions,
                    hideImport: hideImport,
                    cols: dynamicColumns,
                    onNewGroup: handleNewGroup,
                    onNewPosition: (postPosition) =>
                      handleNewPosition(defaultPosition, postPosition),
                    onSelectProduct: (postPosition) =>
                      setOpenProducts(
                        postPosition ? 'afterDivider' : 'beforeDivider'
                      ),
                    onSelectService: (postPosition) =>
                      setOpenServices(
                        postPosition ? 'afterDivider' : 'beforeDivider'
                      ),
                    onNewDiscount: () => setOpenDiscount(!openDiscount),
                    onNewPercent: (postPosition) =>
                      handleNewPosition(
                        {
                          ...defaultPosition,
                          type: 'percent',
                        },
                        postPosition
                      ),
                    onNewHOAI: () => setOpenHOAI({ defaultTaxRate }),
                    onNewTimeBilling: (postPosition) =>
                      setOpenTimeBilling(
                        postPosition ? 'afterDivider' : 'beforeDivider'
                      ),
                    onImport: () => setOpenImport(true),
                    sum: sum,
                    skonto: skonto,
                  }}
                  //followOutput={items?.length > 5}
                  followOutput={false}
                  style={{ width: '100%', height: '80vh' }}
                  data={items}
                  defaultItemHeight={10}
                  fixedHeaderContent={() => (
                    <PositionsHeader
                      collapseModules={collapseModules}
                      columns={dynamicColumns}
                    />
                  )}
                  itemContent={(index, item, context) => null}
                  components={{
                    Table: TableComponent,
                    TableRow: TableRowComponent,
                  }}
                />
              </Box>
              {createPortal(
                <DragOverlay modifiers={[customOffset]} zIndex={9999}>
                  {activeItem ? (
                    <PositionsPlaceholder
                      collapseModules={collapseModules}
                      index={activeItem.index}
                      items={items}
                      item={activeItem}
                      columns={dynamicColumns}
                      imageType={imageType}
                      imageApiFilter={imageApiFilter}
                      descriptionVars={descriptionVars}
                      showVars={showVars}
                      openAmount={openAmount}
                    />
                  ) : null}
                </DragOverlay>,
                document.body
              )}
            </Box>
          </SortableContext>
        </DndContext>

        <ExoDialog
          open={Boolean(openProducts)}
          onClose={() => setOpenProducts(null)}
        >
          <SelectProduct
            onSelect={(product) =>
              handleProductSelect(product, openProducts === 'afterDivider')
            }
            onClose={() => setOpenProducts(null)}
          />
        </ExoDialog>
        <ExoDialog
          open={Boolean(openServices)}
          onClose={() => setOpenServices(null)}
        >
          <SelectService
            onSelect={(service) =>
              handleServiceSelect(service, openServices === 'afterDivider')
            }
            onClose={() => setOpenServices(null)}
          />
        </ExoDialog>
        {openDiscount && (
          <PositionDiscount
            sum={sum}
            open={openDiscount}
            onClose={() => setOpenDiscount(false)}
            onCreate={handleNewDiscount}
          />
        )}
        <ExoDialog
          open={Boolean(openTimeBilling)}
          onClose={() => setOpenTimeBilling(null)}
          limitWidth
        >
          <SelectTimeBilling
            onAddPositions={(pos) =>
              handleNewPositions(pos, openTimeBilling === 'afterDivider')
            }
            onClose={() => setOpenTimeBilling(null)}
          />
        </ExoDialog>
        <ExoDialog open={openImport} onClose={() => setOpenImport(false)}>
          <SelectImportPositions onSubmit={handleImportPositions} />
        </ExoDialog>
        <Dialog open={alertState.alertOpen}>
          <Alert
            severity={'warning'}
            className=" max-w-full w-96 flex flex-col items-center gap-2"
            sx={{
              bgcolor: 'transparent',
              border: `1px solid ${colors.info}80`,
              '.MuiAlert-icon': {
                margin: 0,
                svg: {
                  width: '50px',
                  height: '50px',
                },
              },
            }}
          >
            {
              <Typography className="w-full text-center">
                {t('Also delete all positions belonging to this group?')}
              </Typography>
            }

            <Box
              className="flex flex-row justify-between mt-4 w-full gap-6"
              sx={{
                justifyContent: 'space-between',
              }}
            >
              <Button
                onClick={() => setAlertState({ alertOpen: false, item: null })}
                color="error"
                variant="outlined"
              >
                {t('Cancel')}
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleGroupDelete(alertState.item, false)}
              >
                {t('No')}
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleGroupDelete(alertState.item, true)}
              >
                {t('Yes')}
              </Button>
            </Box>
          </Alert>
        </Dialog>
      </Box>
    );
  }
);

export default ExoPositions;

const PositionsPlaceholder = ({
  item,
  items,
  columns,
  collapseModules,
  imageType,
  imageApiFilter,
  index,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  return (
    <table>
      <tbody>
        <tr
          className={item.orderId ? 'order-position' : ''}
          style={{
            background: item.isGroup ? colors.selected : colors.bgInput,
            opacity: 0.7,
          }}
        >
          {collapseModules && <td className="w-[37px]"></td>}
          {columns.map(
            (col, i) =>
              item.keyId && (
                <Entry
                  key={'' + item.keyId + item.id + i}
                  imageType={imageType}
                  imageApiFilter={imageApiFilter}
                  col={col}
                  item={items[index]}
                  onChange={() => {}}
                >
                  {item[col.key]}
                </Entry>
              )
          )}
        </tr>
      </tbody>
    </table>
  );
};

const TableComponent = (props) => (
  <table {...props} style={{ width: '100%' }} />
);

const TableRowComponent = ({ context, ...props }) => {
  const { 'data-index': index } = props;
  const item = context.items[index];

  return context.items.length != 0 ? (
    item.isSum ? (
      <>
        {context.hidePostPositions && (
          <Controls
            hideGroups={context.hideGroups}
            hideSum={context.hideSum}
            hideServices={context.hideServices}
            hideProducts={context.hideProducts}
            hideNewPosition={context.hideNewPosition}
            hideDiscount={context.hideDiscount}
            hideHOAI={context.hideHOAI}
            hideTimeBilling={context.hideTimeBilling}
            hidePostPos={context.hidePostPositions}
            hideImport={context.hideImport}
            cols={context.cols}
            onNewGroup={context.onNewGroup}
            onNewPosition={context.onNewPosition}
            onSelectProduct={context.onSelectProduct}
            onSelectService={context.onSelectService}
            onNewDiscount={context.onNewDiscount}
            onNewPercent={context.onNewPercent}
            onNewHOAI={context.onNewHOAI}
            onNewTimeBilling={context.onNewTimeBilling}
            onImport={context.onImport}
          />
        )}
        {!context.hideSum && (
          <Sum
            sum={context.sum}
            cols={context.dynamicColumns}
            collapseModules={context.collapseModules}
            skonto={context.skonto}
          />
        )}
      </>
    ) : (
      <Position
        key={item.keyId}
        props={props}
        index={index}
        item={item}
        context={context}
      />
    )
  ) : (
    <PositionsRowEmpty />
  );
};
